import { BinSize, mBinSize } from "./binSize";
import { EntityKind } from "./entityKind";
import { QuantileType } from "./quantileType";
// Type-only: data.schema imports the match-mode constants back out of this
// file, and eliding this import keeps that from becoming a runtime cycle.
import type { ForecastTrumpParamsType } from "./schemas/data.schema";

export type DataType = "Text" | "Number" | "Integer" | "Date";

export type PerformanceDateRange = { startDate: string; endDate: string };

export enum DataEnum {
  Text = "Text",
  Number = "Number",
  Integer = "Integer",
  Date = "Date",
  DefaultProductNumber = "DefaultProductNumber",
  DefaultDate = "DefaultDate",
  None = "None",
  Reset = "Reset"
}

export enum ColumnGroup {
  Dates = "Dates",
  General = "General"
}

export enum Format {
  Year = "YYYY",
  Month = "YYYY-MM",
  Day = "YYYY-MM-DD",
  YearQuarter = "YYYY-QQ",
  None = "None",
  Number = "Number"
}

export const BIN_TYPES = {
  BinSize: "BinSize",
  Quantile: "Quantile",
  Dynamic: "Dynamic"
} as const;

export type BinType = "BinSize" | "Quantile" | "Dynamic";

export type MaxBinsSortOrder = "WellCount" | "Production" | "FieldSum";

export interface QuantileState {
  quantileType: QuantileType;
  numQuantiles: number;
}

export enum AggregationType {
  Default = "Default",
  Sum = "Sum",
  Average = "Average",
  Min = "Min",
  Max = "Max",
  MostCommon = "MostCommon",
  JoinDistinct = "JoinDistinct"
}

const NUMERIC_AGGREGATIONS: ReadonlySet<AggregationType> = new Set([
  AggregationType.Sum,
  AggregationType.Average,
  AggregationType.Min,
  AggregationType.Max
]);

const TEXT_AGGREGATIONS: ReadonlySet<AggregationType> = new Set([
  AggregationType.MostCommon,
  AggregationType.JoinDistinct
]);

/**
 * Returns `true` when `aggregation` produces meaningful results for `dataType`.
 *
 * Numeric aggregations (Sum / Average / Min / Max) require a numeric column
 * because they call `to_f64_opt()` on each value — text/date values yield
 * `None` and the group collapses to empty.
 *
 * Text aggregations (MostCommon / JoinDistinct) require string values;
 * applying them to pure numeric columns loses precision or returns `None`.
 *
 * `Default` is always compatible — the backend resolves it per-column type.
 */
export function isAggregationCompatible(
  aggregation: AggregationType,
  dataType: DataType
): boolean {
  if (aggregation === AggregationType.Default) return true;

  // Integer supports every aggregation: numeric ops work because the values
  // are numbers, and text ops like MostCommon are meaningful for discrete
  // integer values.  This matches the backend's from_proto() which lets both
  // families pass through for Integer columns.
  if (dataType === "Integer") return true;

  const isNumeric = dataType === "Number";

  if (NUMERIC_AGGREGATIONS.has(aggregation)) return isNumeric;
  if (TEXT_AGGREGATIONS.has(aggregation)) return !isNumeric;

  return true;
}

/**
 * Carries the active group-mode through when the user switches focus fields,
 * resetting the aggregation if it is incompatible with the new focus field's
 * data type.  The aggregation is tied to the **focus field** (inner property)
 * — e.g. switching from a numeric focus (Sum) to a text focus must reset to
 * Default because Sum is meaningless for string values.
 *
 * Pass `newFocusDataType` as the data type of the incoming focus field so that
 * an incompatible aggregation is cleared.  Omitting it preserves the mode
 * unchanged (safe fallback for callers that cannot supply a type).
 *
 * Returns `undefined` when there is no active mode so the caller can
 * simply assign `fieldGb.mode = preserveGroupMode(groupBy.mode, fieldGb.dataType)`.
 */
export function preserveGroupMode(
  currentMode: GroupModeOptionsDto | undefined,
  newFocusDataType?: DataType
): GroupModeOptionsDto | undefined {
  if (!currentMode) return undefined;
  if (newFocusDataType === undefined) return currentMode;
  const agg = currentMode.aggregation ?? AggregationType.Default;
  if (!isAggregationCompatible(agg, newFocusDataType)) {
    return { ...currentMode, aggregation: AggregationType.Default };
  }
  return currentMode;
}

/**
 * Start of every shapefile field key
 * (`Shapefile_{projectId}_{shapefileId}_{btoa(columnName)}`). Anything that
 * decides "is this a shapefile field" tests against this one constant.
 */
export const SHAPEFILE_PROPERTY_PREFIX = "Shapefile_";

/**
 * Reserved column name for a focus on the shapefile boundary itself rather than
 * one of its columns: every well that matches the shapefile lands in one group
 * named after the shapefile, everything else in n/a. A DBF column name cannot
 * contain a null byte, so this can never collide with a real column.
 */
export const SHAPEFILE_BOUNDARY_COLUMN = "\u0000boundary";

/**
 * Builds a shapefile field key. The column name is base64-encoded so that a
 * name carrying an underscore still leaves the ids parseable — see
 * utils/column/parseShapefileProperty, which splits the segments back apart.
 */
export function buildShapefileProperty(
  projectId: string,
  shapefileId: string,
  columnName: string
): string {
  return `${SHAPEFILE_PROPERTY_PREFIX}${projectId}_${shapefileId}_${btoa(columnName)}`;
}

const SHAPEFILE_BOUNDARY_SUFFIX = `_${btoa(SHAPEFILE_BOUNDARY_COLUMN)}`;

/**
 * True for the boundary key. Callers use it to keep the reserved column name
 * off the screen: the boundary focus displays as the shapefile's name, so the
 * label paths that would otherwise prefix a field with the shapefile name skip
 * it and the raw sentinel is never rendered.
 */
export function isShapefileBoundaryProperty(property?: Maybe<string>): boolean {
  return (
    !!property &&
    property.startsWith(SHAPEFILE_PROPERTY_PREFIX) &&
    property.endsWith(SHAPEFILE_BOUNDARY_SUFFIX)
  );
}

/**
 * Drops boundary columns from a column list. Nothing builds one any more — the
 * shapefile filtering toggle writes its predicate without a column — but a
 * stale column persisted by an older session would otherwise surface as a row
 * named after the reserved sentinel. One helper so every picker and tree that
 * lists columns applies the same guard.
 */
export function withoutShapefileBoundaryColumns<
  T extends { readonly property?: Maybe<string> }
>(columns: readonly T[]): T[] {
  return columns.filter((column) => !isShapefileBoundaryProperty(column.property));
}

/**
 * How a shapefile predicate decides whether a well matches a feature.
 * "intersecting" is the long-standing behaviour (any sampled point of the
 * lateral inside a feature counts); "containedWithin" requires the whole
 * lateral. Absent means "intersecting" — never write it explicitly, so saved
 * filters and snapshots keep round-tripping unchanged.
 */
export type ShapefileMatchMode = "intersecting" | "containedWithin";

export const SHAPEFILE_MATCH_MODE = {
  Intersecting: "intersecting",
  ContainedWithin: "containedWithin"
} as const;

/** True only for the non-default mode, so callers never test the string twice. */
export function isContainedWithin(mode?: Maybe<ShapefileMatchMode>): boolean {
  return mode === SHAPEFILE_MATCH_MODE.ContainedWithin;
}

/**
 * Returns a copy of `base` carrying the mode. The key is stripped first and
 * only re-added for "containedWithin", so switching back to intersecting clears
 * whatever an earlier switch wrote instead of leaving it behind — an absent
 * field is what "intersecting" means on the wire, see the ShapefileMatchMode
 * doc. Predicates are the only thing that carries a mode; a focus never does.
 */
export function withShapefileMatchMode<T extends object>(
  base: T,
  mode?: Maybe<ShapefileMatchMode>
): T {
  const next = { ...base } as T & { shapefileMatchMode?: ShapefileMatchMode };
  delete next.shapefileMatchMode;
  if (isContainedWithin(mode)) {
    next.shapefileMatchMode = SHAPEFILE_MATCH_MODE.ContainedWithin;
  }
  return next;
}

export interface GroupModeOptionsDto {
  property: string;
  title?: string;
  dataType?: DataType;
  /** Whether the group property supports binning (REQ-UI-GRP-003). */
  canBin?: boolean;
  bin?: mBinSize;
  aggregation?: AggregationType;
  performanceDateRange?: PerformanceDateRange;
}

export interface IGroupBy {
  bin: mBinSize;
  canBin: boolean;
  categoryId: number;
  dataType: DataType;
  display: string;
  entityKind: EntityKind;
  groupByField: string;
  property: string;
  title: string;
  tooltip: string;
  pdenSource: string;

  defaultUnit?: string;
  forecastFolder?: string;
  // True when the user chose this forecastFolder themselves rather than it being derived from
  // the Forecasts panel. It lives here, beside the value it qualifies, so it is saved and
  // restored with the axis — a component-lifetime flag would be lost on remount while
  // forecastFolder survived, and the widen would re-stamp a deliberate pick on every reload.
  // Cleared when the panel selection changes, which supersedes an earlier pick.
  forecastFolderExplicit?: boolean;
  // Set aside by the forecast toggle while forecast is off, so turning it back on restores the
  // same qualification instead of a bare marker. Persisted with the axis for the same reason as
  // forecastFolderExplicit: a component-lifetime value would be lost on remount, and the suffix
  // cannot be rebuilt from forecastFolder alone.
  forecastPropertySuffix?: string;
  forecastTrumpingSettings?: ForecastTrumpParamsType;
  fullContextTitleWithNormalization?: string;
  group?: string;
  isForecastToggleOn?: boolean;
  partialContextTitle?: string;
  product?: string;
  subgroup?: string;
  folderName?: string;
  units?: string[];
  mode?: GroupModeOptionsDto;
  performanceDateRange?: PerformanceDateRange;
  // Geo/pad-metrics radius+source and Fluid Analysis first/last-test tokens, carried
  // through from IGroupByListColumn so stripFieldContext (EVA-127) can locate the same
  // suffix tokens updateGroupBy already baked into `title` — see getFieldContextSuffixTokens.
  metadata?: {
    [key: string]: string;
  };
}

export interface GroupBy extends IGroupBy {
  bin: BinSize;
}

export interface IGroupByMap {
  property: string;
  title: string;
}

export interface ICategoryFieldModel {
  defaultUnit?: string;
  id: string;
  normalizeByDefault?: number;
  property: string;
  title: string;
  tooltip?: string;
  pythonScript?: string;
  subgroup: string;
  group: string;
}

export interface IGroupByListColumn {
  canBin: boolean;
  canNormalize: boolean;
  categoryId?: number;
  // Column catalog default for Max Bins; 0 means none (EVA-306).
  maxNumberSeries?: number;
  dataType: DataType;
  defaultUnit?: string;
  units?: Nullable<string[]>; // array of available units
  forecastFolder?: string;
  group: string;
  id: string;
  isForecastToggleOn?: boolean;
  isDisabled?: boolean;
  normalizeByDefault?: number;
  product: string;
  pythonScript?: string;
  property: string;
  title: string;
  source?: string;
  subgroup: string;
  subgroupOrder?: number;
  tooltip?: string;
  withinSubgroupOrder?: number;
  fieldType?: string;
  published?: boolean;
  metadata?: {
    [key: string]: string;
  };
}

export interface IGroupByListItem {
  columns: IGroupByListColumn[];
  hexColor: string;
  id?: string;
  name: string;
  section?: string;
  property?: string;
  sortOrder: number;
  metadataFilters?: IGroupByMetadataFilter[];
}

export interface IGroupByMetadataFilter {
  key: string;
  name: string;
  options: string[];
}

export interface IBin {
  LessThan?: number;
  BinSize: number;
  GreaterThan?: number;
  MinSize?: number;
  MaxBins?: number;
  MaxBinsSortOrder?: MaxBinsSortOrder;
  MaxBinsSortField?: string;
}

export interface HoverLegendsPayload {
  hoverLegendItem: string;
  hoverLegendGroup: string;
  hoverLegendId: string;
  groupByTitle?: string;
  hoverLegendItemUnfiltered?: string;
}

export interface IGroupByState extends HoverLegendsPayload {
  categoryList: IGroupByListItem[];
  facilityFields: IGroupByListItem[];
  cumPlusForecast: boolean;
  globalGroupBy: IGroupBy;
  globalFacilityFocus: IGroupBy;
  hoverDataPoint: (string | number)[];
  presetList: IGroupBy[];
  recentWellFocus: IGroupBy[];
  recentFacilityFocus: IGroupBy[];
  selectedGroups: string[];
  selectedFacilityGroups: string[];
  hasCheckedBins: boolean;
  hasCheckedBinsFacility: boolean;
  placeholderBinsEnabled: boolean;
  selectedProjectIdForColumns: string | null;
  isLoadingProjectColumns: boolean;
  isRefreshingCategoriesWell: boolean;
  isRefreshingCategoriesFacility: boolean;
  activeFocus: EntityKind;
}
